// web/src/components/browser/Browser.tsx
// Top-level browser component. Resolves the path from the URL,
// fetches the dir listing, renders grid or list, hosts the context
// menu, upload dropzone, and upload progress subscribers.

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useDirListing } from "../../hooks/useDirListing";
import { useDragDrop } from "../../hooks/useDragDrop";
import { useInvalidateFolderThumbnails } from "../../hooks/useThumbnail";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useNavigation } from "../../stores/navigation";
import { useSession } from "../../stores/session";
import { useVault } from "../../hooks/useVault";
import { GridView } from "./GridView";
import { ListView } from "./ListView";
import { EmptyState } from "./EmptyState";
import { ContextMenu, type ContextMenuState } from "./ContextMenu";
import { ItemActionSheet } from "./ItemActionSheet";
import { FileInfoSheet } from "./FileInfoSheet";
import { UploadDropzone } from "./UploadDropzone";
import { UploadConfirmDialog } from "./UploadConfirmDialog";
import { ExportDialog } from "./ExportDialog";
import { NewFolderSheet, type NewFolderMode } from "./NewFolderSheet";
import { SelectionActionBar } from "./SelectionActionBar";
import { MoveToPicker } from "./MoveToPicker";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { Fab } from "./Fab";
import { autoSuffix } from "../../lib/folderNames";
import { scheduleDelete, type DeleteHandle } from "../../lib/deleteUndo";

export function Browser() {
  const { vaultId } = useParams();
  const session = useSession((s) => s.session);
  const location = useLocation();

  // Sessions are in-memory only (no plaintext at rest), so a page
  // refresh lands here with no unlocked handle. The single-lock
  // master-password model means /master is the only re-auth surface
  // — preserving returnTo so the master-unlock lands the user back
  // where they were.
  if (!session || session.vaultId !== vaultId) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/master?returnTo=${returnTo}`} replace />;
  }
  return <BrowserInner />;
}

function BrowserInner() {
  const { vaultId, "*": splat } = useParams();
  const navigate = useNavigate();
  const folderPath = decodeURIComponent(splat ?? "");
  const absPath = `/${folderPath}`.replace(/\/+/g, "/");
  const isMobile = useIsMobile();
  const view = useNavigation((s) => s.view);
  const selection = useNavigation((s) => s.selection);
  const selectionActive = useNavigation((s) => s.selectionActive);
  const clearSelection = useNavigation((s) => s.clearSelection);

  const worker = useVault();
  const handle = useSession((s) => s.session?.handle ?? null);

  const { data, isLoading, refetch } = useDirListing(absPath);
  const invalidateFolderThumbnails = useInvalidateFolderThumbnails();

  const siblings = useMemo(
    () => (data ? data.entries.map((e) => e.name) : []),
    [data],
  );

  // Clear selection when navigating to a different folder.
  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absPath]);

  // ————— context menu / item action sheet —————
  const [ctx, setCtx] = useState<ContextMenuState | null>(null);
  const [kebabTarget, setKebabTarget] = useState<string | null>(null);
  const [fileInfoTarget, setFileInfoTarget] = useState<string | null>(null);

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      // Touch devices can synthesize contextmenu from long-press; on
      // mobile we use ItemActionSheet + SelectionActionBar instead so
      // suppress the desktop menu entirely.
      if (isMobile) return;
      e.preventDefault();
      const targets = Array.from(selection);
      if (targets.length === 0) return;
      setCtx({ x: e.clientX, y: e.clientY, targets });
    },
    [selection, isMobile],
  );

  // ————— flow state —————
  const [newFolder, setNewFolder] = useState<
    | { mode: NewFolderMode; target?: string }
    | null
  >(null);
  const [moveTargets, setMoveTargets] = useState<string[] | null>(null);
  const [deleteConfirmTargets, setDeleteConfirmTargets] = useState<
    { name: string; itemCount: number }[] | null
  >(null);
  const pendingDelete = useRef<DeleteHandle | null>(null);

  useEffect(() => {
    return () => {
      // If the component unmounts while a delete is scheduled (e.g., the
      // user navigated away), cancel it. Navigating away is an implicit
      // "undo" — don't commit a deletion the user has walked back from.
      pendingDelete.current?.cancel();
    };
  }, []);

  // ————— upload —————
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const { isDragging } = useDragDrop(({ files, rejectedDirectories }) => {
    if (rejectedDirectories.length > 0) {
      toast.error(
        `Folder uploads are not supported yet. Skipped: ${rejectedDirectories.join(", ")}`,
      );
    }
    if (files.length > 0) setPendingFiles(files);
  });
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const captureInputRef = useRef<HTMLInputElement | null>(null);
  useRefListen(uploadInputRef, (files) => setPendingFiles(files));

  // Desktop "New folder" button event.
  useEffect(() => {
    const handler = () => setNewFolder({ mode: "create" });
    window.addEventListener("vaultwisp:new-folder", handler);
    return () => window.removeEventListener("vaultwisp:new-folder", handler);
  }, []);

  // ————— export —————
  const [exportTargets, setExportTargets] = useState<string[] | null>(null);

  // ————— action handlers —————
  const joinAbs = (name: string) => `${absPath === "/" ? "" : absPath}/${name}`;

  const doCreateFolder = async (parentPath: string, name: string) => {
    if (handle === null) return;
    try {
      const joined = parentPath === "/" ? `/${name}` : `${parentPath}/${name}`;
      await worker.mkdir(handle, joined);
      await refetch();
    } catch (err) {
      toast.error(`Could not create folder: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const doRename = async (oldName: string, newName: string) => {
    if (handle === null || oldName === newName) return;
    try {
      await worker.renameEntry(handle, joinAbs(oldName), newName);
      await worker.renameThumbnailEntry(handle, absPath, oldName, newName);
      await refetch();
      invalidateFolderThumbnails(absPath);
    } catch (err) {
      toast.error(`Could not rename: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const doMove = async (destPath: string, targets: string[]) => {
    if (handle === null) return;
    try {
      const destListing = await worker.listDir(handle, destPath);
      const destNames = new Set(destListing.entries.map((e) => e.name));
      const thumbItems: Array<{ srcName: string; dstName: string }> = [];
      let renames = 0;
      for (const name of targets) {
        const siblingArray = [...destNames];
        const finalName = autoSuffix(name, siblingArray);
        if (finalName !== name) renames++;
        const src = joinAbs(name);
        const dst = destPath === "/" ? `/${finalName}` : `${destPath}/${finalName}`;
        await worker.moveEntry(handle, src, dst);
        destNames.add(finalName);
        thumbItems.push({ srcName: name, dstName: finalName });
      }
      await worker.moveThumbnailEntries(handle, absPath, destPath, thumbItems);
      await refetch();
      invalidateFolderThumbnails(absPath);
      invalidateFolderThumbnails(destPath);
      if (renames > 0) {
        toast.success(
          `Moved ${targets.length} item${targets.length === 1 ? "" : "s"}. ${renames} renamed to avoid collisions.`,
        );
      } else {
        toast.success(`Moved ${targets.length} item${targets.length === 1 ? "" : "s"}.`);
      }
    } catch (err) {
      toast.error(`Move failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const commitDelete = async (targets: string[]) => {
    if (handle === null) return;
    try {
      for (const name of targets) {
        await worker.deleteEntry(handle, joinAbs(name));
      }
      await refetch();
      invalidateFolderThumbnails(absPath);
    } catch (err) {
      toast.error(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
      await refetch();
    }
  };

  const beginDelete = (targets: string[]) => {
    // Flushing the prior pending delete is fire-and-forget. The worker
    // serialises operations on the same handle (see vault-wasm handle
    // registry), so the two commitDelete calls won't overlap at the
    // wasm boundary even though we don't await here.
    void pendingDelete.current?.flush();
    clearSelection();
    const handleObj = scheduleDelete({
      delayMs: 6000,
      onCommit: async () => {
        await commitDelete(targets);
      },
    });
    pendingDelete.current = handleObj;
    toast(
      `Deleted ${targets.length} item${targets.length === 1 ? "" : "s"}.`,
      {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: () => {
            handleObj.cancel();
            void refetch();
          },
        },
      },
    );
  };

  const requestDelete = async (targets: string[]) => {
    if (handle === null) return;
    const counts = await Promise.all(
      targets.map(async (name) => {
        const entry = data?.entries.find((e) => e.name === name);
        if (!entry || entry.kind !== "dir") return { name, itemCount: 0 };
        const listing = await worker.listDir(handle, joinAbs(name));
        const count = listing.entries.filter((e) => !e.name.startsWith(".")).length;
        return { name, itemCount: count };
      }),
    );
    const nonEmpty = counts.filter((c) => c.itemCount > 0);
    if (nonEmpty.length > 0) {
      setDeleteConfirmTargets(counts);
      return;
    }
    beginDelete(targets);
  };

  const singleSelected = selection.size === 1 ? [...selection][0]! : null;

  return (
    <div
      className="relative pb-24 md:pb-4"
      onContextMenu={onContextMenu}
    >
      <input
        ref={uploadInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          if (fs.length > 0) setPendingFiles(fs);
          e.target.value = "";
        }}
      />
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          if (fs.length > 0) setPendingFiles(fs);
          e.target.value = "";
        }}
      />
      {isDragging && <UploadDropzone folderPath={folderPath} />}

      {isLoading && (
        <div className="p-10 text-center text-sm text-text-dim">Loading…</div>
      )}
      {data &&
        (data.entries.filter((e) => !e.name.startsWith(".")).length === 0 ? (
          <EmptyState folderPath={folderPath} />
        ) : view === "grid" ? (
          <GridView
            vaultId={vaultId!}
            folderPath={folderPath}
            entries={data.entries}
            onTapFile={(name) => setFileInfoTarget(name)}
            onTapPhoto={(name) => {
              const path = `${absPath === "/" ? "" : absPath}/${name}`;
              const encoded = path
                .split("/")
                .filter((s) => s.length > 0)
                .map(encodeURIComponent)
                .join("/");
              navigate(`/v/${vaultId}/view/${encoded}`);
            }}
          />
        ) : (
          <ListView
            vaultId={vaultId!}
            folderPath={folderPath}
            entries={data.entries}
            onKebab={isMobile ? setKebabTarget : undefined}
            onTapFile={(name) => setFileInfoTarget(name)}
            onTapPhoto={(name) => {
              const path = `${absPath === "/" ? "" : absPath}/${name}`;
              const encoded = path
                .split("/")
                .filter((s) => s.length > 0)
                .map(encodeURIComponent)
                .join("/");
              navigate(`/v/${vaultId}/view/${encoded}`);
            }}
          />
        ))}

      {!selectionActive && (
        <Fab
          onNewFolder={() => setNewFolder({ mode: "create" })}
          onUpload={() => uploadInputRef.current?.click()}
          onCapturePhoto={
            isMobile
              ? () => captureInputRef.current?.click()
              : undefined
          }
        />
      )}

      {selectionActive && (
        <SelectionActionBar
          count={selection.size}
          onMove={() => setMoveTargets([...selection])}
          onRename={() => {
            const name = singleSelected;
            if (!name) return;
            const entry = data?.entries.find((e) => e.name === name);
            setNewFolder({
              mode: entry?.kind === "dir" ? "rename-dir" : "rename-file",
              target: name,
            });
          }}
          onExport={() => setExportTargets([...selection])}
          onDelete={() => {
            void requestDelete([...selection]);
          }}
          onCancel={clearSelection}
        />
      )}

      <ContextMenu
        state={ctx}
        onClose={() => setCtx(null)}
        onExport={() => {
          setExportTargets(ctx?.targets ?? []);
          setCtx(null);
        }}
        onPreview={() => {
          toast.info("Preview is coming in M10.");
          setCtx(null);
        }}
        onRename={() => {
          const name = ctx?.targets[0];
          if (!name) return setCtx(null);
          const entry = data?.entries.find((e) => e.name === name);
          setNewFolder({
            mode: entry?.kind === "dir" ? "rename-dir" : "rename-file",
            target: name,
          });
          setCtx(null);
        }}
        onMove={() => {
          setMoveTargets(ctx?.targets ?? []);
          setCtx(null);
        }}
        onDelete={() => {
          void requestDelete(ctx?.targets ?? []);
          setCtx(null);
        }}
        onCopyChunkId={() => {
          toast.info("Copy chunk id — coming in M10.");
          setCtx(null);
        }}
      />

      {kebabTarget !== null && (
        <ItemActionSheet
          open
          onOpenChange={(next) => {
            if (!next) setKebabTarget(null);
          }}
          title={kebabTarget}
          single
          onExport={() => setExportTargets([kebabTarget])}
          onPreview={() => toast.info("Preview is coming in M10.")}
          onRename={() => {
            const entry = data?.entries.find((e) => e.name === kebabTarget);
            setNewFolder({
              mode: entry?.kind === "dir" ? "rename-dir" : "rename-file",
              target: kebabTarget,
            });
          }}
          onMove={() => setMoveTargets([kebabTarget])}
          onDelete={() => {
            void requestDelete([kebabTarget]);
          }}
          onCopyChunkId={() =>
            toast.info("Copy chunk id — coming in M10.")
          }
        />
      )}

      {fileInfoTarget !== null && (() => {
        const entry = data?.entries.find((e) => e.name === fileInfoTarget);
        return (
          <FileInfoSheet
            open
            onOpenChange={(next) => {
              if (!next) setFileInfoTarget(null);
            }}
            fileName={fileInfoTarget}
            mime={entry?.mime}
            sizeBytes={entry?.size}
            modifiedEpochSeconds={entry?.modified}
            onExport={() => {
              setExportTargets([fileInfoTarget]);
              setFileInfoTarget(null);
            }}
            onRename={() => {
              setNewFolder({ mode: "rename-file", target: fileInfoTarget });
              setFileInfoTarget(null);
            }}
            onMove={() => {
              setMoveTargets([fileInfoTarget]);
              setFileInfoTarget(null);
            }}
            onDelete={() => {
              const target = fileInfoTarget;
              setFileInfoTarget(null);
              void requestDelete([target]);
            }}
          />
        );
      })()}

      {newFolder && (
        <NewFolderSheet
          mode={newFolder.mode}
          destPath={absPath}
          siblings={siblings}
          initialName={newFolder.mode === "create" ? "" : (newFolder.target ?? "")}
          open
          onOpenChange={(next) => {
            if (!next) setNewFolder(null);
          }}
          onSubmit={async (name) => {
            if (newFolder.mode === "create") {
              await doCreateFolder(absPath, name);
            } else if (newFolder.target) {
              await doRename(newFolder.target, name);
              clearSelection();
            }
          }}
        />
      )}

      {moveTargets !== null && handle !== null && (
        <MoveToPicker
          open
          onOpenChange={(next) => {
            if (!next) setMoveTargets(null);
          }}
          handle={handle}
          sourceParent={absPath}
          sourceNames={moveTargets}
          count={moveTargets.length}
          listDir={(h, p) => worker.listDir(h, p)}
          onCreateFolder={doCreateFolder}
          onMoveHere={(destPath) => {
            const targets = moveTargets;
            setMoveTargets(null);
            clearSelection();
            void doMove(destPath, targets);
          }}
        />
      )}

      {deleteConfirmTargets && (
        <DeleteConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeleteConfirmTargets(null);
          }}
          targets={deleteConfirmTargets}
          onConfirm={() => {
            beginDelete(deleteConfirmTargets.map((t) => t.name));
            setDeleteConfirmTargets(null);
          }}
        />
      )}

      {pendingFiles && (
        <UploadConfirmDialog
          files={pendingFiles}
          folderPath={absPath}
          onCancel={() => setPendingFiles(null)}
          onStarted={() => setPendingFiles(null)}
          onAllDone={() => {
            invalidateFolderThumbnails(absPath);
            void refetch();
          }}
        />
      )}

      {exportTargets && (
        <ExportDialog
          folderPath={absPath}
          fileNames={exportTargets}
          onClose={() => setExportTargets(null)}
        />
      )}
    </div>
  );
}

function useRefListen(
  inputRef: React.MutableRefObject<HTMLInputElement | null>,
  onFiles: (files: File[]) => void,
) {
  // Listen for the Topbar "+ Upload" button event.
  React.useEffect(() => {
    const handler = () => inputRef.current?.click();
    window.addEventListener("vaultwisp:open-upload-picker", handler);
    return () => window.removeEventListener("vaultwisp:open-upload-picker", handler);
  }, [inputRef, onFiles]);
}
